"use client";

import type { SyntheticEvent } from "react";
import { useCallback, useMemo, useState } from "react";
import { TweetSkeleton } from "react-tweet";
import type { Tweet } from "react-tweet/api";
import { CustomEmbeddedTweetClient } from "@/components/tweets/custom-embedded-tweet-client";
import { tweetsApiResponseSchema } from "@/components/tweets/tweets-api-schema";

type LoadState = "idle" | "loading" | "loaded" | "error";
const PAGE_SIZE = 50;

export function DeferredTweetGroup(params: {
  groupId: string;
  title: string;
  description: string | null;
  tweetIds: string[];
  variant?: "default" | "dense";
}) {
  const { groupId, title, description, tweetIds } = params;
  const variant = params.variant ?? "default";
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [tweets, setTweets] = useState<
    Array<{ tweetId: string; tweet: Tweet | null }>
  >([]);
  const [loadedCount, setLoadedCount] = useState(0);

  const nextIds = useMemo(() => {
    return tweetIds.slice(loadedCount, loadedCount + PAGE_SIZE);
  }, [loadedCount, tweetIds]);

  const loadTweets = useCallback(async () => {
    if (loadState === "loading" || loadState === "loaded") {
      return;
    }

    setLoadState("loading");
    try {
      const response = await fetch(
        `/api/tweets?ids=${encodeURIComponent(tweetIds.slice(0, PAGE_SIZE).join(","))}`
      );
      const json: unknown = await response.json();

      const parsed = tweetsApiResponseSchema.safeParse(json);
      if (!(response.ok && parsed.success)) {
        setLoadState("error");
        return;
      }

      setTweets(parsed.data.tweets);
      setLoadedCount(parsed.data.tweets.length);
      setLoadState("loaded");
    } catch {
      setLoadState("error");
    }
  }, [loadState, tweetIds]);

  const loadMore = useCallback(async () => {
    if (loadState !== "loaded") {
      return;
    }
    if (isLoadingMore) {
      return;
    }
    if (nextIds.length === 0) {
      return;
    }

    setIsLoadingMore(true);
    setLoadMoreFailed(false);
    try {
      const response = await fetch(
        `/api/tweets?ids=${encodeURIComponent(nextIds.join(","))}`
      );
      const json: unknown = await response.json();

      const parsed = tweetsApiResponseSchema.safeParse(json);
      if (!(response.ok && parsed.success)) {
        setLoadMoreFailed(true);
        return;
      }

      setTweets((prev) => [...prev, ...parsed.data.tweets]);
      setLoadedCount((prev) => prev + parsed.data.tweets.length);
    } catch {
      setLoadMoreFailed(true);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, loadState, nextIds]);

  const handleToggle = useCallback(
    (event: SyntheticEvent<HTMLDetailsElement>) => {
      const open = event.currentTarget.open;
      if (open) {
        loadTweets();
      }
    },
    [loadTweets]
  );

  const denseSummary = variant === "dense";

  const detailsClassName = denseSummary
    ? "group/tweet-group w-full"
    : "group/tweet-group mx-auto mb-3 w-full max-w-[600px] overflow-hidden rounded-xl border border-[var(--explore-border)] bg-[var(--explore-bg-card)] transition-colors hover:border-[var(--explore-text-muted)]";

  const expandedBodyClassName = denseSummary
    ? "border-[var(--explore-border-light)] border-t px-0 pt-4 pb-5"
    : "border-[var(--explore-border-light)] border-t px-4 py-4";

  return (
    <details
      className={detailsClassName}
      id={`evidence-${groupId}`}
      onToggle={handleToggle}
    >
      {denseSummary ? (
        <summary className="group/summary -mx-1 flex cursor-pointer list-none items-center gap-3 rounded-md px-1 py-2.5 transition-colors hover:bg-[var(--explore-bg-highlight)] [&::-webkit-details-marker]:hidden">
          <span className="min-w-0 flex-1 truncate font-medium text-[0.92rem] text-[var(--explore-text-primary)]">
            {title}
          </span>
          <span className="shrink-0 text-[0.72rem] text-[var(--explore-text-muted)] tabular-nums">
            {tweetIds.length} {tweetIds.length === 1 ? "post" : "posts"}
          </span>
          <svg
            aria-hidden="true"
            className="size-4 shrink-0 text-[var(--explore-text-muted)] transition-transform group-open/tweet-group:rotate-90"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              d="M9 5l7 7-7 7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </summary>
      ) : (
        <summary className="cursor-pointer px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold text-[0.95rem] text-[var(--explore-text-primary)]">
              {title}
            </span>
            <span className="shrink-0 text-[0.75rem] text-[var(--explore-text-muted)]">
              {tweetIds.length} {tweetIds.length === 1 ? "post" : "posts"}
            </span>
          </div>
          {description ? (
            <div className="mt-1.5 text-[0.8rem] text-[var(--explore-text-secondary)] leading-relaxed">
              {description}
            </div>
          ) : null}
        </summary>
      )}

      <div className={expandedBodyClassName}>
        {loadState === "idle" ? (
          <button
            className="rounded-md border border-[var(--explore-border)] bg-[var(--explore-bg)] px-3 py-1.5 font-semibold text-[0.8rem] text-[var(--explore-text-secondary)] transition-colors hover:text-[var(--explore-text-primary)]"
            onClick={() => {
              loadTweets();
            }}
            type="button"
          >
            Load tweets
          </button>
        ) : null}

        {loadState === "loading" ? (
          <div className="space-y-4">
            <TweetSkeleton />
            <TweetSkeleton />
          </div>
        ) : null}

        {loadState === "error" ? (
          <div className="space-y-3">
            <div className="text-[0.85rem] text-[var(--explore-text-secondary)]">
              Failed to load tweets.
            </div>
            <button
              className="rounded-md border border-[var(--explore-border)] bg-[var(--explore-bg)] px-3 py-1.5 font-semibold text-[0.8rem] text-[var(--explore-text-secondary)] transition-colors hover:text-[var(--explore-text-primary)]"
              onClick={() => {
                loadTweets();
              }}
              type="button"
            >
              Retry
            </button>
          </div>
        ) : null}

        {loadState === "loaded" ? (
          <div className="space-y-6">
            {tweets.map((item) => {
              if (!item.tweet) {
                return (
                  <a
                    className="block text-[0.85rem] text-[var(--explore-text-secondary)] underline decoration-[var(--explore-text-muted)] underline-offset-4 hover:text-[var(--explore-text-primary)]"
                    href={`https://x.com/i/web/status/${item.tweetId}`}
                    key={item.tweetId}
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    View tweet {item.tweetId} on X
                  </a>
                );
              }

              return (
                <div
                  className="[&_.react-tweet-theme]:!bg-transparent"
                  key={item.tweetId}
                >
                  <CustomEmbeddedTweetClient tweet={item.tweet} />
                </div>
              );
            })}

            {loadedCount < tweetIds.length ? (
              <div className="space-y-3">
                {loadMoreFailed ? (
                  <div className="text-[0.85rem] text-[var(--explore-text-secondary)]">
                    Failed to load more tweets.
                  </div>
                ) : null}
                <button
                  className="rounded-md border border-[var(--explore-border)] bg-[var(--explore-bg)] px-3 py-1.5 font-semibold text-[0.8rem] text-[var(--explore-text-secondary)] transition-colors hover:text-[var(--explore-text-primary)]"
                  disabled={isLoadingMore}
                  onClick={() => {
                    loadMore();
                  }}
                  type="button"
                >
                  {isLoadingMore
                    ? "Loading…"
                    : `Load more (${tweetIds.length - loadedCount} remaining)`}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </details>
  );
}
